import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Briefcase,
  Users,
  Eye,
  Plus,
  ArrowRight,
  Building2,
  MapPin,
  Clock,
  MoreVertical,
  Edit,
  Trash2,
  Loader2,
  TrendingUp,
  LayoutGrid,
  Share2,
  Calendar,
  Video,
  LogOut
} from 'lucide-react';
import { useState as useLocalState } from 'react';
import { JobSyndicationPanel } from '../components/JobSyndicationPanel';
import { CalendarConnect } from '../components/CalendarConnect';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

export default function EmployerDashboard() {
  const { api, logout } = useAuth();
  const navigate = useNavigate();
  const handleLogout = () => { logout(); navigate('/'); };
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syndicateJobId, setSyndicateJobId] = useLocalState(null);
  const [showCalendar, setShowCalendar] = useLocalState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, jobsRes] = await Promise.all([
        api.get('/stats/employer'),
        api.get('/jobs/employer')
      ]);
      setStats(statsRes.data);
      setJobs(jobsRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (!window.confirm('Are you sure you want to delete this job?')) return;
    
    try {
      await api.delete(`/jobs/${jobId}`);
      toast.success('Job deleted successfully');
      setJobs(jobs.filter(j => j.id !== jobId));
    } catch (error) {
      toast.error('Failed to delete job');
    }
  };

  const getStatusColor = (status) => {
    return status === 'active' 
      ? 'bg-emerald-100 text-emerald-700' 
      : 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F0F4F8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Skeleton className="h-10 w-64 mb-8" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-32" />)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F0F4F8]" data-testid="employer-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Employer Dashboard</h1>
            <p className="text-muted-foreground mt-1">
              Manage your job postings and candidates
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <Button variant="outline" className="border-gray-300" onClick={() => setShowCalendar(true)}>
              <Calendar className="w-4 h-4 mr-2" />Calendar
            </Button>
            <Link to="/employer/pipeline">
              <Button variant="outline" className="border-[#003366] text-[#003366] hover:bg-[#003366]/10">
                <LayoutGrid className="w-4 h-4 mr-2" />Pipeline
              </Button>
            </Link>
            <Link to="/employer/recruitment-requests">
              <Button variant="outline" className="border-[#003366] text-[#003366] hover:bg-[#003366]/10">
                <Users className="w-4 h-4 mr-2" />Employment Requests
              </Button>
            </Link>
            <Link to="/employer/post-job">
              <Button className="bg-[#E11D48] hover:bg-[#E11D48]/90" data-testid="post-job-btn">
                <Plus className="w-4 h-4 mr-2" />Post New Job
              </Button>
            </Link>
            <Button variant="outline" className="border-gray-300" onClick={handleLogout}>
              <LogOut className="w-4 h-4 mr-2" />Logout
            </Button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Jobs</p>
                  <p className="text-3xl font-bold text-foreground mt-1">
                    {stats?.total_jobs || 0}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-[#003366]/10 flex items-center justify-center">
                  <Briefcase className="w-6 h-6 text-[#003366]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active Jobs</p>
                  <p className="text-3xl font-bold text-emerald-600 mt-1">
                    {stats?.active_jobs || 0}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Applications</p>
                  <p className="text-3xl font-bold text-blue-600 mt-1">
                    {stats?.total_applications || 0}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Views</p>
                  <p className="text-3xl font-bold text-purple-600 mt-1">
                    {stats?.total_views || 0}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                  <Eye className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Jobs List */}
        <Card>
          <CardHeader>
            <CardTitle>Your Job Postings</CardTitle>
          </CardHeader>
          <CardContent>
            {jobs.length > 0 ? (
              <div className="space-y-4">
                {jobs.map((job) => (
                  <div 
                    key={job.id} 
                    className="flex items-center justify-between p-4 rounded-lg border hover:border-[#003366]/30 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-[#F0F4F8] flex items-center justify-center">
                        <Building2 className="w-6 h-6 text-[#003366]" />
                      </div>
                      <div>
                        <Link 
                          to={`/employer/jobs/${job.id}/applicants`}
                          className="font-medium hover:text-[#003366] transition-colors"
                        >
                          {job.title}
                        </Link>
                        <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {job.location}
                          </span>
                          <span className="flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {job.applications_count} applicants
                          </span>
                          <span className="flex items-center gap-1">
                            <Eye className="w-3 h-3" />
                            {job.views} views
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <Badge className={getStatusColor(job.status)}>
                        {job.status}
                      </Badge>
                      
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`job-menu-${job.id}`}>
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem asChild>
                            <Link to={`/employer/jobs/${job.id}/applicants`}>
                              <Users className="w-4 h-4 mr-2" />View Applicants
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link to={`/employer/jobs/${job.id}/edit`}>
                              <Edit className="w-4 h-4 mr-2" />Edit Job
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setSyndicateJobId(job.id)}>
                            <Share2 className="w-4 h-4 mr-2" />Syndicate / Share
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-red-600"
                            onClick={() => handleDeleteJob(job.id)}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />Delete Job
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Briefcase className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-lg font-medium mb-2">No jobs posted yet</p>
                <p className="text-muted-foreground mb-4">
                  Start attracting top talent by posting your first job
                </p>
                <Link to="/employer/post-job">
                  <Button className="bg-[#E11D48] hover:bg-[#E11D48]/90" data-testid="post-first-job-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Post Your First Job
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Application Stats */}
        {stats?.applications_by_status && Object.keys(stats.applications_by_status).length > 0 && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Applications Overview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {['pending', 'reviewed', 'shortlisted', 'rejected', 'hired'].map((status) => (
                  <div key={status} className="text-center p-4 rounded-lg bg-[#F0F4F8]">
                    <div className="text-2xl font-bold text-foreground">
                      {stats.applications_by_status[status] || 0}
                    </div>
                    <div className="text-sm text-muted-foreground capitalize">
                      {status}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Syndication panel */}
      {syndicateJobId && (
        <JobSyndicationPanel jobId={syndicateJobId} onClose={() => setSyndicateJobId(null)} />
      )}

      {/* Calendar connect modal */}
      {showCalendar && (
        <CalendarConnect onClose={() => setShowCalendar(false)} />
      )}
    </div>
  );
}
